// External imports
import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

// Internal imports
import { uploadFile, createTrackingUpdate } from "api";
import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";
import { ReactComponent as FileIcon } from "assets/images/icons/File.svg";
import Button from "components/atoms/Button/Button";
import Textarea from "components/atoms/Textarea/Textarea";
import CardNew from "components/molecules/Card/CardNew";
import Column from "components/atoms/Column/Column";
import Messages from "pages/shipper/ShipmentTracking/components/Chat/components/Messages/Messages";
import usePermissions from "hooks/usePermissions";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import CommentComponentStyles from "constants/CommentComponentStyles";
import initializeWebSocket from "./initializeWebSocket";

import "./Chat.scss";
import UpdatesDropzone from "./components/UpdatesDropzone/UpdatesDropzone";

const Chat = function ({
  shipmentId,
  shipmentDetails,
  isShipmentDetailsLoading,
  trackingUpdates,
  openDeleteMessageDialog,
  refetchTrackingUpdates,
}) {
  const { REACT_APP_TRACKING_WEBSOCKET_URL } = import.meta.env;

  // Hooks
  const { handleSubmit } = useFormContext();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { setValue, getValues, watch } = useFormContext();
  const { permissions } = usePermissions();
  const { websocket_token } = JSON.parse(permissions);

  // State
  const [isTextAreaEmpty, setIsTextAreaEmpty] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [activeUploadsCount, setActiveUploadsCount] = useState(0);

  // Data declarations
  const disableSave = (isTextAreaEmpty && uploadedFiles.length === 0) || activeUploadsCount > 0;

  // Set the textarea to empty if the user deletes the text
  useEffect(() => {
    if (getValues("trackingUpdates")) setIsTextAreaEmpty(false);
    else setIsTextAreaEmpty(true);
  }, [watch("trackingUpdates")]);

  // Initialize the websocket connection
  useEffect(() => {
    if (REACT_APP_TRACKING_WEBSOCKET_URL && websocket_token && shipmentId) {
      initializeWebSocket(REACT_APP_TRACKING_WEBSOCKET_URL, websocket_token, shipmentId, queryClient, intl);
    }
  }, [websocket_token, shipmentId]);

  // The following functions handle the file selection and deletion
  const onFileDelete = (file) => {
    const newUploadedFiles = uploadedFiles.filter((uploadedFile) => uploadedFile.url !== file.url);
    setUploadedFiles(newUploadedFiles);
  };

  /** This function uploads the selected file to the /common/files endpoint
   *  and stores the response in the rhf value "uploadedFile"
   * @param {File} file - The file to be uploaded
   */
  const uploadSelectedFile = (file) => {
    setActiveUploadsCount((prevCount) => prevCount + 1);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", file.name);
    uploadFile(formData)
      .then((res) => {
        if (res.status === 201) {
          setUploadedFiles((uploadedFiles) => [
            ...uploadedFiles,
            {
              url: res.data.url,
              filename: res.data.name,
              size: file.size,
              reference_number: res.data.reference_number,
            },
          ]);
        }
      })
      .catch((error) => {
        errorToast(error);
        setValue("file", "");
      })
      .finally(() => setActiveUploadsCount((prevCount) => prevCount - 1));
  };

  /**
   * Submit the users message to RoadRunner v3
   * @param {*} data - Form data
   * @param {*} e - Event object
   */
  const submitNewMessage = (data) => {

    // If the user has not entered a message, show an error toast
    if (!data.trackingUpdates && uploadedFiles.length === 0) {
      toast(
        intl.formatMessage({
          id: "tracking.pleaseEnterAMessage",
          defaultMessage: "Please enter a message.",
        }),
        { type: "error" },
      );
      return;
    }

    setIsSubmitInProgress(true);

    // Create the new message object
    const newPostData = {
      type: uploadedFiles.length > 0 ? "upload" : "general",
      message: data.trackingUpdates,
      attachments: uploadedFiles,
    };

    // Submit the message
    createTrackingUpdate(shipmentId, newPostData)
      .then(async () => {
        // Reset the form
        setValue("trackingUpdates", "");
        setUploadedFiles([]);

        // Refetch the tracking updates asynchronously
        await new Promise((resolve) => {
          setTimeout(resolve, 1000);
        });
        await refetchTrackingUpdates();
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "tracking.couldNotSubmitMessage",
            defaultMessage: "Could not submit message. Please try again later.",
          }),
        ),
      )
      .finally(() => setIsSubmitInProgress(false));
  };

  return (
    <Column style={{ zIndex: 1, marginBottom: 15 }}>
      <CardNew className="updates-card" isLoading={isShipmentDetailsLoading}>
        <section
          style={{
            zIndex: 400,
          }}
        >
          {/* Chat messages */}
          {!isShipmentDetailsLoading && (
            <Messages
              data={trackingUpdates}
              shipmentID={shipmentId}
              provider={shipmentDetails?.organization?.provider_company}
              shipper={shipmentDetails?.organization?.shipper_company}
              showBookingInstruction={shipmentDetails?.organization?.booking_instruction}
              openDeleteMessageDialog={openDeleteMessageDialog}
            />
          )}
          {/* Input form */}
          <form onSubmit={handleSubmit(submitNewMessage)} className="rounded-bottom">
            <Textarea
              style={{
                ...CommentComponentStyles,
                borderRadius: "10px",
                borderColor: "black",
              }}
              className="updates-textarea"
              name="trackingUpdates"
              placeholder={`${intl.formatMessage({
                id: "general.writeAComment",
                defaultMessage: "Write a message",
              })}...`}
              eventContext="Shipment Dashboard - Tracking"
              rows={2}
              data-testid={testIds.SHIPMENT_DASHBOARD_TRACKING_UPDATE_INPUT}
            />
            <div className="d-flex mt-3 align-items-center">
              <UpdatesDropzone uploadSelectedFile={uploadSelectedFile} setActiveUploadsCount={setActiveUploadsCount} />
              <Button
                type="submit"
                variant="primary"
                className="rounded-pill ml-auto"
                label={intl.formatMessage({ id: "general.send", defaultMessage: "Send" })}
                isLoading={isSubmitInProgress}
                disabled={disableSave}
                style={{
                  fontSize: 12,
                  lineHeight: "14px",
                  padding: "5px 16px",
                  backgroundColor: "#8BCAFD",
                  border: "none",
                }}
                data-testid={testIds.SHIPMENT_DASHBOARD_TRACKING_UPDATE_SUBMIT_BUTTON}
              />
            </div>
            <div>
              {uploadedFiles.length > 0 &&
                uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-center mt-2"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <FileIcon className="mr-2" />
                    <div>{file.filename}</div>

                    {hoveredIndex === index && (
                      <CircleCross
                        stroke="black"
                        onClick={() => onFileDelete(file)}
                        style={{ marginLeft: 6, cursor: "pointer" }}
                      />
                    )}
                  </div>
                ))}
              {/* File loading spinner */}
              {activeUploadsCount > 0 && (
                <FontAwesomeIcon icon={faCircleNotch} className="text-secondary font-12 ml-2" spin />
              )}
            </div>
          </form>
        </section>
      </CardNew>
    </Column>
  );
};

Chat.propTypes = {
  shipmentId: PropTypes.string.isRequired,
  shipmentDetails: PropTypes.instanceOf(Object).isRequired,
  isShipmentDetailsLoading: PropTypes.bool.isRequired,
  trackingUpdates: PropTypes.instanceOf(Array).isRequired,
  openDeleteMessageDialog: PropTypes.func.isRequired,
  refetchTrackingUpdates: PropTypes.func.isRequired,
};

export default Chat;
